import React, { useMemo } from 'react';

import useDataTable from 'src/shared/components/data-table/hooks/useDataTable';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import tableStyles from 'src/shared/components/data-table/DataTable.module.scss';
import TableHeaderCell from './components/table-header-cell/TableHeaderCell';

import styles from './TableHeader.module.scss';

const TableHeader = () => {
    const { columns, data, hiddenColumnIds, isSelectable, selectableColumnIndex, dispatch, rows, selectedRowIds } = useDataTable();

    // Get all unique row IDs from current page
    const uniqueColumnIndex = useMemo(() => columns?.findIndex(column => column.isUnique) ?? -1, [columns]);

    const currentPageRowIds = useMemo(() => {
        if (!rows) {
            return [];
        }
        return rows
            .map(row => {
                // Use the unique column if available, otherwise fall back to rowId
                return (uniqueColumnIndex >= 0 ? row.cells[uniqueColumnIndex] : row.rowId) as string | number;
            })
            .filter(id => id !== undefined && id !== null && id !== '');
    }, [rows, uniqueColumnIndex]);

    // Check if all current page rows are selected
    const allSelected = useMemo(() => {
        if (currentPageRowIds.length === 0) {
            return false;
        }
        return currentPageRowIds.every(id => selectedRowIds.has(id));
    }, [currentPageRowIds, selectedRowIds]);

    if (!columns || !data) {
        return null;
    }

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            dispatch({
                type: 'select_all_rows',
                payload: currentPageRowIds,
            });
        } else {
            dispatch({
                type: 'deselect_all_rows',
            });
        }
    };

    return (
        <thead className={styles.header} data-testid='table-header'>
            <tr>
                {isSelectable && selectableColumnIndex === 0 && (
                    <th key='select-header' className={tableStyles.cellCheckbox}>
                        <Checkbox checked={allSelected} onChange={handleSelectAll} disabled={currentPageRowIds.length === 0} />
                    </th>
                )}
                {columns.map((column, index) => {
                    const currentColumn = columns[index];
                    if (hiddenColumnIds && hiddenColumnIds.has(currentColumn.columnId)) {
                        return null;
                    }

                    return (
                        <React.Fragment key={column.columnId}>
                            <TableHeaderCell {...column} />
                            {isSelectable && selectableColumnIndex === index + 1 && (
                                <th key={`select-${column.columnId}`} className={tableStyles.cellCheckbox}>
                                    <Checkbox checked={allSelected} onChange={handleSelectAll} disabled={currentPageRowIds.length === 0} />
                                </th>
                            )}
                        </React.Fragment>
                    );
                })}
            </tr>
        </thead>
    );
};

export default TableHeader;
